import React, { useState } from 'react';
import { useParams } from 'react-router-dom';
import { Edit, Globe, Eye, EyeOff } from 'lucide-react';
import { useCompany } from '../hooks/useCompany';
import { useUserSettings } from '../hooks/useUserSettings';
import { useAnonymization } from '../utils/anonymize';
import EditCompanyPopup from '../components/companies/EditCompanyPopup';
import ConfirmationPopup from '../components/ui/ConfirmationPopup';
import LoadingPage from '../components/ui/LoadingPage';
import CompanyContacts from '../components/companies/CompanyContacts';
import { Skeleton } from '../components/ui/Skeleton';
import { supabase } from '../lib/supabase';

export default function CompanyDetails() {
  const { id } = useParams<{ id: string }>();
  const { company, loading, error } = useCompany(id!);
  const { settings, loading: settingsLoading } = useUserSettings();
  const [temporaryReveal, setTemporaryReveal] = useState(false);
  const [showEdit, setShowEdit] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  const shouldAnonymize = settings?.anonymizing_mode && !temporaryReveal;

  const handleDelete = async () => {
    try {
      const { error: deleteError } = await supabase
        .from('nrm_companies')
        .delete()
        .eq('id', company?.id);

      if (deleteError) throw deleteError;
      navigate('/companies');
    } catch (err) {
      console.error('Failed to delete company:', err);
    }
  };

  if (loading) {
    return <LoadingPage message="Loading company details..." />;
  }

  if (error) {
    return <div className="text-red-500">{error}</div>;
  }

  if (!company) {
    return <LoadingPage message="Company not found" />;
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h1 className="text-2xl font-bold">{company.name}</h1>
        <div className="flex items-center gap-4">
          <button
            onClick={() => setShowDeleteConfirm(true)}
            className="btn btn-secondary flex items-center gap-2 text-red-500 hover:text-white hover:bg-red-500"
          >
            Delete
          </button>
          <button
            onClick={() => setShowEdit(true)}
            className="btn btn-secondary flex items-center gap-2"
          >
            <Edit className="w-4 h-4" />
            Edit
          </button>
          {company.website && (
            <a
              href={company.website}
              target="_blank"
              rel="noopener noreferrer"
              className="btn btn-primary flex items-center gap-2"
            >
              <Globe className="w-4 h-4" />
              Visit Website
            </a>
          )}
        </div>
      </div>

      <div className="space-y-6">
        <div className="card p-6">
          <h2 className="text-lg font-semibold mb-4">Company Information</h2>
          <div className="space-y-4">
            {settingsLoading ? (
              <Skeleton className="h-8 w-64" />
            ) : (
              <div>
                <p className="text-sm text-gray-400">Name</p>
                <p className="text-lg">
                  {useAnonymization(company.name, 'company', shouldAnonymize)}
                </p>
              </div>
            )}
            {company.industry && (
              <div>
                <p className="text-sm text-gray-400">Industry</p>
                <p className="text-lg">
                  {useAnonymization(company.industry, 'company', shouldAnonymize)}
                </p>
              </div>
            )}
            {company.website && (
              <div>
                <p className="text-sm text-gray-400">Website</p>
                {shouldAnonymize ? (
                  <p className="text-lg">*********.***</p>
                ) : (
                  <a 
                    href={company.website}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-lg text-primary-500 hover:text-primary-400"
                  >
                    {company.website}
                  </a>
                )}
              </div>
            )}
            {company.description && (
              <div>
                <p className="text-sm text-gray-400">Description</p>
                <p className="text-lg">
                  {useAnonymization(company.description, 'company', shouldAnonymize)}
                </p>
              </div>
            )}
          </div>
        </div>

        <CompanyContacts 
          companyId={company.id}
        />
      </div>

      {showEdit && (
        <EditCompanyPopup
          company={company}
          onClose={() => setShowEdit(false)}
          onSuccess={() => setShowEdit(false)}
        />
      )}

      {showDeleteConfirm && (
        <ConfirmationPopup
          title="Delete Company"
          message={`Are you sure you want to delete ${company.name}? This action cannot be undone and will remove all associated data.`}
          confirmLabel="Delete"
          onConfirm={handleDelete}
          onCancel={() => setShowDeleteConfirm(false)}
        />
      )}
    </div>
  );
}